import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Tooltip, IconButton,
  LinearProgress, Switch, FormControlLabel, Divider, InputAdornment,
} from '@mui/material';
import { Tune, Add, Edit, Archive, Search, ExpandMore } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const CATEGORY_CFG: Record<string, { label: string; color: string; emoji: string }> = {
  personal:   { label: 'Personal',   color: '#1565c0', emoji: '👤' },
  employment: { label: 'Employment', color: '#2e7d32', emoji: '💼' },
  compliance: { label: 'Compliance', color: '#6a1b9a', emoji: '📋' },
  payroll:    { label: 'Payroll',    color: '#e65100', emoji: '💰' },
  it_systems: { label: 'IT Systems', color: '#0277bd', emoji: '💻' },
  benefits:   { label: 'Benefits',   color: '#558b2f', emoji: '🎁' },
  security:   { label: 'Security',   color: '#b71c1c', emoji: '🔒' },
  other:      { label: 'Other',      color: '#546e7a', emoji: '📄' },
};

const TYPE_CFG: Record<string, { label: string; emoji: string }> = {
  text:         { label: 'Text',         emoji: '📝' },
  number:       { label: 'Number',       emoji: '🔢' },
  date:         { label: 'Date',         emoji: '📅' },
  dropdown:     { label: 'Dropdown',     emoji: '▼' },
  multi_select: { label: 'Multi-Select', emoji: '☑️' },
  checkbox:     { label: 'Checkbox',     emoji: '✅' },
  url:          { label: 'URL',          emoji: '🔗' },
  email:        { label: 'Email',        emoji: '📧' },
  phone:        { label: 'Phone',        emoji: '📞' },
  textarea:     { label: 'Text Area',    emoji: '📃' },
};

interface FieldDef {
  id: string; name: string; description?: string; fieldKey: string;
  fieldType: string; category: string; isRequired: boolean; isActive: boolean;
  isVisibleToEmployee: boolean; isEditableByEmployee: boolean;
  options?: string[]; placeholder?: string; sortOrder: number;
}

interface FieldStat { id: string; name: string; category: string; fieldType: string; isRequired: boolean; filledCount: number; coveragePct: number }

const emptyForm = {
  name: '', description: '', fieldType: 'text', category: 'other',
  isRequired: false, isVisibleToEmployee: false, isEditableByEmployee: false,
  placeholder: '', options: '', sortOrder: 0,
};

export default function CustomFields() {
  const { user } = useAuth();
  const isAdmin = (user as any)?.role === 'hr_admin';
  const isHR    = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');

  const [tab, setTab]     = useState(0);
  const [defs, setDefs]   = useState<FieldDef[]>([]);
  const [stats, setStats] = useState<FieldStat[]>([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editId, setEditId]     = useState<string | null>(null);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (catFilter) params.set('category', catFilter);
      if (showInactive) params.set('includeInactive', 'true');

      const calls = [apiFetch(`/api/custom-fields/definitions?${params}`)];
      if (isHR && tab === 1) calls.push(apiFetch('/api/custom-fields/definitions/stats'));

      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setDefs(results[0].value ?? []);
      if (results[1] && results[1].status === 'fulfilled') setStats((results[1] as any).value ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab, catFilter, showInactive, isHR]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setFormData({ ...emptyForm }); setFormOpen(true); };
  const openEdit   = (d: FieldDef) => {
    setEditId(d.id);
    setFormData({
      name: d.name, description: d.description || '', fieldType: d.fieldType,
      category: d.category, isRequired: d.isRequired,
      isVisibleToEmployee: d.isVisibleToEmployee, isEditableByEmployee: d.isEditableByEmployee,
      placeholder: d.placeholder || '', options: (d.options || []).join(', '), sortOrder: d.sortOrder,
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        options: formData.options
          ? formData.options.split(',').map(s => s.trim()).filter(Boolean)
          : undefined,
      };
      if (editId) {
        await apiFetch(`/api/custom-fields/definitions/${editId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/custom-fields/definitions', { method: 'POST', body: JSON.stringify(payload) });
      }
      setFormOpen(false); load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const archive = async (id: string) => {
    await apiFetch(`/api/custom-fields/definitions/${id}/archive`, { method: 'PATCH' });
    load();
  };

  const filtered = defs.filter(d => {
    if (!search) return true;
    return d.name.toLowerCase().includes(search.toLowerCase()) ||
           d.fieldKey.toLowerCase().includes(search.toLowerCase());
  });

  const grouped: Record<string, FieldDef[]> = {};
  filtered.forEach(d => {
    if (!grouped[d.category]) grouped[d.category] = [];
    grouped[d.category].push(d);
  });

  const TABS = isHR ? ['Field Definitions', 'Coverage Stats'] : ['Employee Fields'];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#37474f', width: 48, height: 48 }}><Tune /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Custom Employee Fields</Typography>
            <Typography variant="body2" color="text.secondary">
              Define and manage custom profile attributes without code changes
            </Typography>
          </Box>
        </Box>
        {isAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}
            sx={{ bgcolor: '#37474f', '&:hover': { bgcolor: '#263238' } }}>
            New Field
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t, i) => <Tab key={i} label={t} />)}
      </Tabs>

      {/* ── Field Definitions ── */}
      {tab === 0 && (
        <>
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="Search fields…" value={search}
              onChange={e => setSearch(e.target.value)} sx={{ minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <TextField select size="small" label="Category" value={catFilter}
              onChange={e => setCatFilter(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All Categories</MenuItem>
              {Object.entries(CATEGORY_CFG).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
              ))}
            </TextField>
            {isHR && (
              <FormControlLabel control={<Switch size="small" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />}
                label={<Typography variant="body2">Show archived</Typography>} />
            )}
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Tune sx={{ fontSize: 64, opacity: 0.2, mb: 1 }} />
              <Typography>No custom fields defined yet</Typography>
              {isAdmin && <Button variant="contained" sx={{ mt: 2, bgcolor: '#37474f' }} onClick={openCreate}>Create First Field</Button>}
            </Box>
          ) : (
            Object.entries(grouped).map(([cat, fields]) => {
              const cfg = CATEGORY_CFG[cat] ?? CATEGORY_CFG.other;
              return (
                <Box key={cat} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: cfg.color, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {cfg.emoji} {cfg.label}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Field Name</TableCell>
                          <TableCell>Key</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Options / Placeholder</TableCell>
                          <TableCell align="center">Required</TableCell>
                          <TableCell align="center">Visible to Emp</TableCell>
                          <TableCell align="center">Employee Editable</TableCell>
                          {isAdmin && <TableCell align="center">Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields.map(d => (
                          <TableRow key={d.id} hover sx={{ opacity: d.isActive ? 1 : 0.5 }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{d.name}</Typography>
                              {d.description && <Typography variant="caption" color="text.secondary">{d.description}</Typography>}
                              {!d.isActive && <Chip size="small" label="Archived" sx={{ ml: 0.5, fontSize: '0.6rem', bgcolor: '#f5f5f5', color: '#78909c' }} />}
                            </TableCell>
                            <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', px: 0.5, borderRadius: 1 }}>{d.fieldKey}</Typography></TableCell>
                            <TableCell>
                              <Chip size="small" label={`${TYPE_CFG[d.fieldType]?.emoji} ${TYPE_CFG[d.fieldType]?.label ?? d.fieldType}`}
                                sx={{ fontSize: '0.65rem' }} />
                            </TableCell>
                            <TableCell>
                              {d.options?.length
                                ? <Typography variant="caption">{d.options.slice(0, 3).join(', ')}{d.options.length > 3 ? ` +${d.options.length - 3}` : ''}</Typography>
                                : <Typography variant="caption" color="text.secondary">{d.placeholder || '—'}</Typography>}
                            </TableCell>
                            <TableCell align="center">{d.isRequired ? '✅' : '—'}</TableCell>
                            <TableCell align="center">{d.isVisibleToEmployee ? '👁️' : '—'}</TableCell>
                            <TableCell align="center">{d.isEditableByEmployee ? '✏️' : '—'}</TableCell>
                            {isAdmin && (
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(d)}><Edit fontSize="small" /></IconButton></Tooltip>
                                  {d.isActive && <Tooltip title="Archive"><IconButton size="small" onClick={() => archive(d.id)}><Archive fontSize="small" /></IconButton></Tooltip>}
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })
          )}
        </>
      )}

      {/* ── Coverage Stats ── */}
      {tab === 1 && isHR && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Filled</TableCell>
                <TableCell align="center">Required</TableCell>
                <TableCell>Coverage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? <CircularProgress size={20} /> : 'No data yet — add some field values to see coverage.'}
                </TableCell></TableRow>
              )}
              {stats.map(s => {
                const cat = CATEGORY_CFG[s.category] ?? CATEGORY_CFG.other;
                return (
                  <TableRow key={s.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{s.name}</Typography></TableCell>
                    <TableCell>
                      <Chip size="small" label={`${cat.emoji} ${cat.label}`}
                        sx={{ bgcolor: cat.color + '15', color: cat.color, fontSize: '0.65rem' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{TYPE_CFG[s.fieldType]?.emoji} {TYPE_CFG[s.fieldType]?.label}</Typography>
                    </TableCell>
                    <TableCell align="right">{s.filledCount}</TableCell>
                    <TableCell align="center">{s.isRequired ? '✅' : '—'}</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate" value={s.coveragePct}
                          sx={{ flex: 1, height: 8, borderRadius: 4,
                            '& .MuiLinearProgress-bar': { bgcolor: s.coveragePct >= 80 ? '#2e7d32' : s.coveragePct >= 50 ? '#f57f17' : '#b71c1c' } }} />
                        <Typography variant="caption">{s.coveragePct}%</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Custom Field' : 'New Custom Field'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Field Name" value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} required
              placeholder="e.g. Security Clearance Level" />
            <TextField select label="Category" value={formData.category}
              onChange={e => setFormData(p => ({ ...p, category: e.target.value }))} sx={{ minWidth: 160 }}>
              {Object.entries(CATEGORY_CFG).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField fullWidth label="Description (optional)" value={formData.description}
            onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="Field Type" value={formData.fieldType}
              onChange={e => setFormData(p => ({ ...p, fieldType: e.target.value }))}>
              {Object.entries(TYPE_CFG).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth label="Sort Order" type="number" value={formData.sortOrder}
              onChange={e => setFormData(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))}
              sx={{ maxWidth: 120 }} />
          </Box>
          {(formData.fieldType === 'dropdown' || formData.fieldType === 'multi_select') && (
            <TextField fullWidth label="Options (comma-separated)" value={formData.options}
              onChange={e => setFormData(p => ({ ...p, options: e.target.value }))}
              placeholder="Option 1, Option 2, Option 3" />
          )}
          {!['dropdown', 'multi_select', 'checkbox', 'date'].includes(formData.fieldType) && (
            <TextField fullWidth label="Placeholder text (optional)" value={formData.placeholder}
              onChange={e => setFormData(p => ({ ...p, placeholder: e.target.value }))} />
          )}
          <Divider />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel control={<Switch checked={formData.isRequired} onChange={e => setFormData(p => ({ ...p, isRequired: e.target.checked }))} />}
              label="Required" />
            <FormControlLabel control={<Switch checked={formData.isVisibleToEmployee} onChange={e => setFormData(p => ({ ...p, isVisibleToEmployee: e.target.checked }))} />}
              label="Visible to Employee" />
            <FormControlLabel control={<Switch checked={formData.isEditableByEmployee} onChange={e => setFormData(p => ({ ...p, isEditableByEmployee: e.target.checked }))} />}
              label="Employee Editable" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !formData.name}
            sx={{ bgcolor: '#37474f' }}>
            {saving ? 'Saving…' : editId ? 'Update' : 'Create Field'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
